// components/MyPage/UserSettings/PasswordChangeForm.jsx
import { useState } from 'react';
import {
    Card, CardContent, Typography,
    TextField, IconButton, InputAdornment,
    Stack, useMediaQuery, Dialog, DialogTitle,
    DialogContent, DialogActions, Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import api from '../../../api/axios';

export default function PasswordChangeForm({ setSnack }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    /* 비밀번호 변경용 상태 */
    const [oldPw, setOldPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [newPwConfirm, setNewPwConfirm] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [saving, setSaving] = useState(false);

    /* 탈퇴용 상태 */
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmPhrase, setConfirmPhrase] = useState('');
    const [deleting, setDeleting] = useState(false);

    const mismatch = newPw && newPwConfirm && newPw !== newPwConfirm;
    const confirmOk = confirmPhrase.trim() === '회원 탈퇴';   // 🔑 문구 일치 여부

    /* ── 비밀번호 변경 ─────────────────────── */
    const handleChange = () => {
        if (saving || !oldPw || !newPw || !newPwConfirm || mismatch) return;
        setSaving(true);

        api.post('/api/accounts/user/change-password/', {
            old_password: oldPw,
            new_password: newPw,
        })
            .then(() => {
                setOldPw(''); setNewPw(''); setNewPwConfirm('');
                setSnack({ open: true, msg: '변경 완료!', severity: 'success' });
            })
            .catch(err => {
                const backendMsg = err.response?.data?.message;
                setSnack({
                    open: true,
                    msg: backendMsg
                        ? backendMsg.replace('현재 비밀번호가', '현재 비밀번호가\n')
                        : '현재 비밀번호가\n틀렸습니다.',
                    severity: 'error',
                });
            })
            .finally(() => setSaving(false));
    };

    /* ── 회원 탈퇴 ─────────────────────────── */
    const handleDeleteAccount = () => {
        if (!confirmOk) return;
        setDeleting(true);

        api.post('/api/accounts/user/delete/', {
            password: oldPw,                         // ← 유지하고 싶지 않으면 제거
            confirm_phrase: confirmPhrase.trim(),
        })
            .then(() => {
                setSnack({ open: true, msg: '회원 탈퇴가 완료되었습니다.', severity: 'success' });
                window.location.href = '/login';       // 필요 시 리다이렉트
            })
            .catch(() => {
                setSnack({
                    open: true,
                    msg: '탈퇴 처리 중 오류가 발생했습니다.',
                    severity: 'error',
                });
            })
            .finally(() => {
                setDeleting(false);
                setConfirmOpen(false);
                setConfirmPhrase('');
            });
    };

    /* ───────── JSX ────────── */
    return (
        <>
            <Card>
                <CardContent>
                    <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold" sx={{ mb: 3 }}>
                        🔒 비밀번호 변경
                    </Typography>

                    {/* 비밀번호 필드 */}
                    <Stack spacing={2} sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            type={showPw ? 'text' : 'password'}
                            label="현재 비밀번호"
                            value={oldPw}
                            onChange={e => setOldPw(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPw(p => !p)}>
                                            {showPw ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            type={showPw ? 'text' : 'password'}
                            label="새 비밀번호"
                            value={newPw}
                            onChange={e => setNewPw(e.target.value)}
                        />
                    </Stack>

                    <TextField
                        fullWidth
                        type={showPw ? 'text' : 'password'}
                        label="새 비밀번호 확인"
                        value={newPwConfirm}
                        onChange={e => setNewPwConfirm(e.target.value)}
                        error={mismatch}
                        helperText={mismatch ? '비밀번호가 일치하지 않습니다.' : ' '}
                    />

                    {/* 버튼 영역 */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
                        <LoadingButton
                            variant="contained"
                            fullWidth
                            loading={saving}
                            onClick={handleChange}
                        >
                            비밀번호 변경
                        </LoadingButton>

                        <LoadingButton
                            variant="outlined"
                            color="error"
                            fullWidth
                            loading={deleting}
                            onClick={() => setConfirmOpen(true)}
                        >
                            회원 탈퇴
                        </LoadingButton>
                    </Stack>
                </CardContent>
            </Card>

            {/* 탈퇴 확인 다이얼로그 */}
            <Dialog open={confirmOpen} onClose={() => !deleting && setConfirmOpen(false)}>
                <DialogTitle>정말 탈퇴하시겠습니까?</DialogTitle>

                <DialogContent dividers>
                    <Typography gutterBottom>
                        탈퇴 시 모든 정보가 삭제되며, 복구가 불가능합니다.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        진행하려면 아래 입력창에 <b>‘회원 탈퇴’</b>를 정확히 입력해 주세요.
                    </Typography>

                    <TextField
                        fullWidth
                        label="확인 문구 입력"
                        value={confirmPhrase}
                        onChange={e => setConfirmPhrase(e.target.value)}
                        disabled={deleting}
                        autoFocus
                    />
                </DialogContent>

                <DialogActions>
                    <Button disabled={deleting} onClick={() => setConfirmOpen(false)}>
                        취소
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleting || !confirmOk}
                        onClick={handleDeleteAccount}
                    >
                        탈퇴
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
